// ============================================
// ReviewPanel — Thomas / JT Attorney Review
// Right-side sliding chat panel for guided
// document review sessions.
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ReviewSession } from '../../../types/docops';
import { C } from './docopsConstants';

// ── Parse CHOICES: block from Thomas message ──
function parseChoices(content: string): { body: string; choices: string[] } {
  const choicesRe = /\n?CHOICES:\s*\n((?:\s*-\s*.+\n?)+)/i;
  const match = content.match(choicesRe);
  if (!match) return { body: content, choices: [] };
  const body = content.slice(0, match.index).trimEnd();
  const choices = match[1]
    .split('\n')
    .map(l => l.replace(/^\s*-\s*/, '').trim())
    .filter(l => l.length > 0);
  return { body, choices };
}

// Meta-choices are follow-up actions, not issue-resolving decisions
const META_CHOICES = /^(give me more detail|skip for now|show me the details|more info|tell me more)/i;
function isDecisionChoice(choice: string): boolean {
  return !META_CHOICES.test(choice.trim());
}

interface ReviewPanelProps {
  session: ReviewSession;
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  onSendMessage: (message: string, resolveIssue?: boolean) => void;
  onSkipIssue: () => void;
  onEndReview: () => void;
  onGenerateFinal: () => void;
  onRestartReview?: () => void;
}

const GOLD = '#C9A96E';

export const ReviewPanel: React.FC<ReviewPanelProps> = ({
  session,
  isLoading,
  isGenerating,
  error,
  onSendMessage,
  onSkipIssue,
  onEndReview,
  onGenerateFinal,
  onRestartReview,
}) => {
  const [input, setInput] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastMsgRef = useRef<HTMLDivElement>(null);
  const prevMsgCount = useRef(session.conversationHistory.length);

  // Scroll to TOP of newest Thomas message (not bottom of chat)
  useEffect(() => {
    const msgs = session.conversationHistory;
    if (msgs.length > prevMsgCount.current && lastMsgRef.current) {
      lastMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevMsgCount.current = msgs.length;
  }, [session.conversationHistory.length]);

  // Also scroll when loading indicator appears
  useEffect(() => {
    if (isLoading && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [isLoading]);

  // Focus input after Thomas responds
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  const handleSend = () => {
    const msg = input.trim();
    if (!msg || isLoading) return;
    setInput('');
    onSendMessage(msg, false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const resolvedCount = session.issues.filter(i => i.resolved).length;
  const totalIssues = session.issues.length;
  const progress = totalIssues > 0 ? Math.round((resolvedCount / totalIssues) * 100) : 100;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: '#FFFFFF', borderLeft: `1px solid ${C.borderLight}`,
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${C.borderLight}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: GOLD }}>Thomas / JT</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>
            {session.phase === 'briefing' && 'Document Briefing'}
            {session.phase === 'review' && `Issue ${Math.min(session.currentIssueIndex + 1, totalIssues)} of ${totalIssues}`}
            {session.phase === 'variables' && 'Variable Resolution'}
            {session.phase === 'consolidation' && 'Decision Summary'}
            {session.phase === 'complete' && 'Review Complete'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {onRestartReview && (
            <button
              onClick={onRestartReview}
              disabled={isLoading || isGenerating}
              style={{
                background: 'none', border: `1px solid ${GOLD}`, borderRadius: 3,
                padding: '4px 10px', fontSize: 11, color: GOLD,
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
                opacity: isLoading || isGenerating ? 0.5 : 1,
              }}
            >
              New Session
            </button>
          )}
          <button
            onClick={onEndReview}
            style={{
              background: 'none', border: `1px solid ${C.border}`, borderRadius: 3,
              padding: '4px 10px', fontSize: 11, color: C.textMuted,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {totalIssues > 0 && (
        <div style={{ padding: '8px 16px 4px', flexShrink: 0 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginBottom: 4,
            fontSize: 10, color: C.textMuted, fontWeight: 600,
          }}>
            <span>{resolvedCount} of {totalIssues} resolved</span>
            <span>{progress}%</span>
          </div>
          <div style={{
            height: 4, background: C.borderLight, borderRadius: 2, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', width: `${progress}%`,
              background: GOLD, borderRadius: 2,
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} style={{
        flex: 1, overflowY: 'auto', padding: '12px 16px',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {session.conversationHistory.map((msg, idx) => {
          const isThomas = msg.role === 'thomas';
          const isLastMsg = idx === session.conversationHistory.length - 1;
          const { body, choices } = isThomas ? parseChoices(msg.content) : { body: msg.content, choices: [] };
          const showChoiceButtons = isThomas && isLastMsg && choices.length > 0 && !isLoading;

          return (
            <div
              key={idx}
              ref={isLastMsg ? lastMsgRef : undefined}
              style={{
                alignSelf: msg.role === 'attorney' ? 'flex-end' : 'flex-start',
                maxWidth: '88%',
              }}
            >
              <div style={{
                padding: '10px 14px',
                borderRadius: msg.role === 'attorney' ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                background: msg.role === 'attorney' ? C.text : '#F8FAFC',
                color: msg.role === 'attorney' ? '#FFFFFF' : C.text,
                fontSize: 13, lineHeight: 1.55,
              }}>
                <div className="thomas-msg" style={{
                  fontSize: 13,
                  color: msg.role === 'attorney' ? '#FFFFFF' : C.text,
                }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
                </div>
              </div>

              {/* Choice buttons — only on last Thomas message */}
              {showChoiceButtons && (
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, padding: '0 4px',
                }}>
                  {choices.map((choice, ci) => (
                    <button
                      key={ci}
                      onClick={() => onSendMessage(choice, isDecisionChoice(choice))}
                      style={{
                        padding: '7px 16px', borderRadius: 20,
                        border: ci === 0 ? 'none' : `1px solid ${C.border}`,
                        background: ci === 0 ? GOLD : '#FFFFFF',
                        color: ci === 0 ? '#FFFFFF' : C.textSec,
                        fontSize: 12, fontWeight: ci === 0 ? 600 : 500,
                        cursor: 'pointer', fontFamily: 'inherit',
                        transition: 'opacity 0.15s',
                      }}
                      onMouseOver={(e) => { (e.target as HTMLElement).style.opacity = '0.8'; }}
                      onMouseOut={(e) => { (e.target as HTMLElement).style.opacity = '1'; }}
                    >
                      {choice}
                    </button>
                  ))}
                </div>
              )}

              <div style={{
                fontSize: 9, color: C.textMuted, marginTop: 3,
                textAlign: msg.role === 'attorney' ? 'right' : 'left',
                padding: '0 4px',
              }}>
                {msg.role === 'thomas' ? 'Thomas' : 'You'} &middot; {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          );
        })}

        {/* Loading indicator */}
        {isLoading && (
          <div style={{ alignSelf: 'flex-start', maxWidth: '88%' }}>
            <div style={{
              padding: '10px 14px', borderRadius: '12px 12px 12px 3px',
              background: '#F8FAFC', color: C.textMuted, fontSize: 13,
            }}>
              <span style={{ display: 'inline-flex', gap: 4 }}>
                <span className="thomas-dot" style={{ animationDelay: '0ms' }} />
                <span className="thomas-dot" style={{ animationDelay: '200ms' }} />
                <span className="thomas-dot" style={{ animationDelay: '400ms' }} />
              </span>
            </div>
          </div>
        )}

        {/* Generating final indicator */}
        {isGenerating && (
          <div style={{
            alignSelf: 'center', padding: '12px 20px',
            background: '#FFFBEB', border: `1px solid ${GOLD}`,
            borderRadius: 4, fontSize: 12, color: GOLD, fontWeight: 600,
            textAlign: 'center',
          }}>
            Thomas is regenerating the final document...
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '8px 12px', background: C.redBg, border: `1px solid ${C.red}`,
            borderRadius: 3, fontSize: 12, color: C.red,
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Input area */}
      <div style={{
        padding: '12px 16px', borderTop: `1px solid ${C.borderLight}`,
        flexShrink: 0, background: '#FFFFFF',
      }}>
        {session.phase === 'complete' ? (
          <div style={{ display: 'flex', gap: 8 }}>
            {!session.completedAt || session.decisions.length > 0 ? (
              <button
                onClick={onGenerateFinal}
                disabled={isGenerating}
                style={{
                  flex: 1, padding: '10px 16px', borderRadius: 3,
                  background: GOLD, color: C.text, border: 'none',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit',
                  opacity: isGenerating ? 0.6 : 1,
                }}
              >
                {isGenerating ? 'Generating...' : 'Generate Final Version'}
              </button>
            ) : (
              <div style={{
                flex: 1, padding: '10px 16px', textAlign: 'center',
                fontSize: 13, color: C.success, fontWeight: 600,
              }}>
                Final document generated. Ready for approval.
              </div>
            )}
            <button
              onClick={onEndReview}
              style={{
                padding: '10px 16px', borderRadius: 3,
                background: 'none', border: `1px solid ${C.border}`,
                fontSize: 13, color: C.textSec, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading || isGenerating}
                placeholder="Type your response or click a button above..."
                rows={2}
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: 3,
                  border: `1px solid ${C.border}`, fontSize: 13,
                  fontFamily: 'inherit', resize: 'none',
                  color: C.text, background: '#FFFFFF',
                  outline: 'none',
                }}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading || isGenerating}
                style={{
                  padding: '8px 16px', borderRadius: 3, alignSelf: 'flex-end',
                  background: input.trim() && !isLoading ? GOLD : C.borderLight,
                  color: input.trim() && !isLoading ? C.text : C.textMuted,
                  border: 'none', fontSize: 13, fontWeight: 600,
                  cursor: input.trim() && !isLoading ? 'pointer' : 'default',
                  fontFamily: 'inherit',
                }}
              >
                Send
              </button>
            </div>
            {/* Action row */}
            <div style={{
              display: 'flex', gap: 8, marginTop: 8,
              justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ fontSize: 10, color: C.textMuted }}>
                Shift+Enter for new line
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {session.phase === 'review' && totalIssues > 1 && (
                  <button
                    onClick={onSkipIssue}
                    disabled={isLoading}
                    style={{
                      background: 'none', border: `1px solid ${C.border}`, borderRadius: 3,
                      padding: '3px 10px', fontSize: 11, color: C.textMuted,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Skip Issue
                  </button>
                )}
                <button
                  onClick={onEndReview}
                  style={{
                    background: 'none', border: `1px solid ${C.border}`, borderRadius: 3,
                    padding: '3px 10px', fontSize: 11, color: C.textMuted,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  End Review
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
